import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { twMerge } from 'tailwind-merge'
import Icon from '../Icon'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { modalCreators } from '@/store/ducks/modal'

interface ModalProps {
	name: string
	children?: React.ReactNode
	className?: string
	disabledOnClose?: boolean
	onClose?: () => void
}

export default function Modal({
	name,
	children,
	className,
	disabledOnClose,
	onClose,
}: ModalProps) {
	const dispatch = useAppDispatch()
	const { modalName } = useAppSelector((state) => state.modal)

	async function closeModal() {
		onClose && onClose()
		await dispatch(modalCreators.closeModal())
	}

	return (
		<Transition appear show={modalName === name} as={Fragment}>
			<Dialog
				as='div'
				className='relative z-50'
				onClose={() => (disabledOnClose ? null : closeModal())}
			>
				<Transition.Child
					as={Fragment}
					enter='ease-out duration-300'
					enterFrom='opacity-0'
					enterTo='opacity-100'
					leave='ease-in duration-200'
					leaveFrom='opacity-100'
					leaveTo='opacity-0'
				>
					<div className='fixed inset-0 bg-[#000000]/80' />
				</Transition.Child>

				<div className='fixed inset-0 overflow-y-auto'>
					<div className='flex min-h-full items-center justify-center p-4 text-center'>
						<Transition.Child
							as={Fragment}
							enter='ease-out duration-300'
							enterFrom='opacity-0 scale-95'
							enterTo='opacity-100 scale-100'
							leave='ease-in duration-200'
							leaveFrom='opacity-100 scale-100'
							leaveTo='opacity-0 scale-95'
						>
							<Dialog.Panel
								id='sidebar-content'
								className={twMerge(
									'w-full max-w-md max-h-[90vh] overflow-auto transform rounded-md bg-zinc-950 border border-zinc-700 p-6 align-middle shadow-xl transition-all',
									className
								)}
							>
								{(() => {
									if (disabledOnClose) return <></>

									return (
										<button
											onClick={() => closeModal()}
											className='absolute top-3 h-10 w-10 right-3 p-[2px] rounded-full flex items-center justify-center bg-[#000000]/40 hover:bg-[#000000]/60 transition-all duration-200 z-50'
										>
											<Icon
												name='close-modal'
												className='text-white'
												width='20px'
												height='20px'
											/>
										</button>
									)
								})()}
								{children}
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	)
}
